import React, { useState, useEffect } from 'react';
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabaseClient';
import SEO from './SEO';
import { getSEOConfig } from '../utils/seoConfig';
import '../styles/ArtworkPublicPage.css';

const ArtworkPublicPage = () => {
  const [searchParams] = useSearchParams();
  const { artId: paramArtId } = useParams();
  const navigate = useNavigate();
  
  // Support both query param (?artId=123) and URL param (/artwork/123)
  // Also support FlutterFlow legacy parameters
  const artId = paramArtId || searchParams.get('artId') || searchParams.get('art_id');
  
  const [artwork, setArtwork] = useState(null);
  const [artist, setArtist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchArtworkData = async () => {
      if (!artId) {
        setError('No artwork ID provided');
        setLoading(false);
        return;
      }

      try {
        // Fetch artwork details with enhanced query
        const { data: artworkData, error: artworkError } = await supabase
          .from('artworks')
          .select(`
            *,
            artists (
              id,
              full_name,
              first_name,
              last_name,
              handle,
              profile_image,
              bio,
              website,
              instagram,
              facebook,
              twitter
            ),
            collections (
              id,
              title,
              description,
              cover_image
            )
          `)
          .eq('id', artId)
          .eq('is_public', true)
          .single();

        if (artworkError) {
          // Try alternative table names if main query fails
          const { data: altArtworkData, error: altError } = await supabase
            .from('artwork')
            .select(`
              *,
              artist:artist_id (
                id,
                full_name,
                first_name,
                last_name,
                handle,
                profile_image,
                bio
              )
            `)
            .eq('id', artId)
            .single();

          if (altError) throw artworkError;
          
          // Transform alternative data structure
          setArtwork({
            ...altArtworkData,
            artists: altArtworkData.artist
          });
          setArtist(altArtworkData.artist);
        } else {
          setArtwork(artworkData);
          setArtist(artworkData?.artists);
        }
      } catch (err) {
        console.error('Error fetching artwork:', err);
        setError('Failed to load artwork');
      } finally {
        setLoading(false);
      }
    };

    fetchArtworkData();
  }, [artId]);

  // Handle legacy FlutterFlow route redirects
  useEffect(() => {
    if (artId && !loading && !error && artwork) {
      // If we're on the legacy route, we could optionally redirect to the modern route
      // For now, we'll keep both working to preserve SEO
      const currentPath = window.location.pathname;
      if (currentPath === '/feelArtStory' || currentPath === '/artStory') {
        // Optionally set canonical URL for SEO
        const canonicalLink = document.createElement('link');
        canonicalLink.rel = 'canonical';
        canonicalLink.href = `${window.location.origin}/artwork/${artId}`;
        document.head.appendChild(canonicalLink);
      }
    }
  }, [artId, loading, error, artwork]);

  if (loading) {
    return (
      <div className="artwork-public-loading">
        <div className="loading-spinner">Loading artwork...</div>
      </div>
    );
  }

  if (error || !artwork) {
    return (
      <div className="artwork-public-error">
        <h1>Artwork Not Found</h1>
        <p>{error || 'The artwork you\'re looking for doesn\'t exist.'}</p>
        <button 
          onClick={() => navigate('/')}
          className="btn btn-primary"
        >
          Return to Home
        </button>
      </div>
    );
  }

  return (
    <div className="artwork-public-page">
      <SEO {...getSEOConfig.artwork({
        title: artwork.title,
        artist_name: artist?.full_name || artist?.first_name,
        description: artwork.description,
        image_url: artwork.primary_image || artwork.image_url,
        medium: artwork.medium,
        style: artwork.style
      })} />

      <div className="artwork-hero">
        <div className="artwork-image-container">
          <img 
            src={artwork.primary_image || artwork.image_url} 
            alt={artwork.title}
            className="artwork-main-image"
          />
        </div>
        
        <div className="artwork-details">
          <h1 className="artwork-title">{artwork.title}</h1>
          
          {artist && (
            <div className="artist-info">
              <h2 className="artist-name">
                <a href={`/artist?handle=${artist.handle}`} className="artist-link">
                  {artist.full_name || `${artist.first_name} ${artist.last_name}`}
                </a>
              </h2>
              {artist.bio && <p className="artist-bio">{artist.bio}</p>}
            </div>
          )}
          
          <div className="artwork-meta">
            {artwork.medium && (
              <div className="meta-item">
                <strong>Medium:</strong> {artwork.medium}
              </div>
            )}
            {artwork.style && (
              <div className="meta-item">
                <strong>Style:</strong> {artwork.style}
              </div>
            )}
            {artwork.dimensions && (
              <div className="meta-item">
                <strong>Dimensions:</strong> {artwork.dimensions}
              </div>
            )}
            {artwork.year && (
              <div className="meta-item">
                <strong>Year:</strong> {artwork.year}
              </div>
            )}
          </div>
          
          {artwork.description && (
            <div className="artwork-description">
              <h3>Description</h3>
              <p>{artwork.description}</p>
            </div>
          )}
          
          {artwork.price && (
            <div className="artwork-pricing">
              <h3>Pricing</h3>
              <p className="price">${artwork.price.toLocaleString()}</p>
            </div>
          )}
        </div>
      </div>
      
      {/* Additional artwork images */}
      {artwork.additional_images && artwork.additional_images.length > 0 && (
        <div className="artwork-gallery">
          <h3>Additional Views</h3>
          <div className="gallery-grid">
            {artwork.additional_images.map((image, index) => (
              <img 
                key={index}
                src={image} 
                alt={`${artwork.title} - View ${index + 1}`}
                className="gallery-image"
              />
            ))}
          </div>
        </div>
      )}
      
      {/* Artist social links */}
      {artist && (artist.website || artist.instagram || artist.facebook || artist.twitter) && (
        <div className="artist-social">
          <h3>Connect with {artist.first_name}</h3>
          <div className="social-links">
            {artist.website && (
              <a href={artist.website} target="_blank" rel="noopener noreferrer" className="social-link">
                Website
              </a>
            )}
            {artist.instagram && (
              <a href={`https://instagram.com/${artist.instagram}`} target="_blank" rel="noopener noreferrer" className="social-link">
                Instagram
              </a>
            )}
            {artist.facebook && (
              <a href={`https://facebook.com/${artist.facebook}`} target="_blank" rel="noopener noreferrer" className="social-link">
                Facebook
              </a>
            )}
            {artist.twitter && (
              <a href={`https://twitter.com/${artist.twitter}`} target="_blank" rel="noopener noreferrer" className="social-link">
                Twitter
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ArtworkPublicPage;
